import { useCallback, useEffect, useState } from 'react'
import { KeyRound, Plus, Timer } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PERMISSIONS } from '@/lib/permissions'
import { PermissionDenied, ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchRequests,
  requestAccess,
  approveRequest,
  denyRequest,
  revokeRequest,
  SCOPES,
  JIT_STR as J,
  REQ_STATUS_LABEL,
  type AccessRequest,
} from './jitApi'

const statusPill: Record<string, string> = {
  pending: 'bg-warning/10 text-warning',
  approved: 'bg-success/10 text-success',
  denied: 'bg-surface-3 text-tertiary',
  revoked: 'bg-danger/10 text-danger',
  expired: 'bg-surface-3 text-tertiary',
}

/**
 * A23.07 — JIT huquqlar (just-in-time). JONLI /admin/jit/requests ustidan:
 * so'rov → four-eyes tasdiq → vaqt-cheklangan grant (avto-isteqfo). Break-glass
 * favqulodda oqimi ALOHIDA ekranda (A18.08 /jit/break-glass). Majburlash serverda.
 */
export function StaffJITScreen() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [status, setStatus] = useState('')
  const [data, setData] = useState<AccessRequest[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [scope, setScope] = useState('')
  const [reason, setReason] = useState('')
  const [dur, setDur] = useState(60)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    setData(null)
    fetchRequests(status, ac.signal)
      .then(setData)
      .catch((e) => {
        if (!ac.signal.aborted)
          setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, status])

  const submit = async () => {
    if (!scope.trim()) return
    setBusy(true)
    try {
      await requestAccess(scope.trim(), reason.trim())
      setReason('')
      toast(J.done, { tone: 'success' })
      reload()
    } catch {
      toast(J.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      toast(J.done, { tone: 'success' })
      reload()
    } catch (e) {
      toast(e instanceof ApiError && e.isForbidden ? J.fourEyes : J.failed, { tone: 'error' })
    }
  }

  if (!can(PERMISSIONS.JIT_REQUEST)) return <PermissionDenied permission={PERMISSIONS.JIT_REQUEST} />

  return (
    <>
      <PageHeader title={J.title} screenId="A23.07" description={J.desc} verified />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* So'rov formasi */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <input
              list="staff-jit-scopes"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder={J.scope}
              className="rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <datalist id="staff-jit-scopes">
              {SCOPES.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={J.reason}
              className="min-w-[160px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={submit}
              disabled={busy || !scope.trim()}
              className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50"
            >
              <Plus size={14} />
              {J.request}
            </button>
          </div>

          {/* Filtr + grant muddati */}
          <div className="flex items-center justify-between text-sm">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-secondary"
            >
              <option value="">{J.all}</option>
              <option value="pending">{J.pending}</option>
              <option value="approved">{J.approved}</option>
              <option value="denied">{J.denied}</option>
              <option value="revoked">{J.revoked}</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs text-tertiary">
              {J.duration}:
              <select
                value={dur}
                onChange={(e) => setDur(Number(e.target.value))}
                className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-secondary"
              >
                <option value={60}>60</option>
                <option value={240}>240</option>
                <option value={480}>480</option>
                <option value={1440}>1440</option>
              </select>
            </label>
          </div>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : data.length === 0 ? (
            <EmptyState icon={<Timer size={28} strokeWidth={1.5} />} title={J.empty} />
          ) : (
            <div className="space-y-2">
              {data.map((r) => (
                <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <KeyRound size={13} className="shrink-0 text-tertiary" />
                        <span className="font-mono text-sm text-primary">{r.scope}</span>
                        <span className={`rounded px-1.5 py-0.5 text-xs ${statusPill[r.status] ?? ''}`}>
                          {REQ_STATUS_LABEL[r.status] ?? r.status}
                        </span>
                        {r.active && <span className="text-xs text-success">● {J.active}</span>}
                      </div>
                      {r.reason && <p className="mt-1 text-sm text-secondary">{r.reason}</p>}
                      <p className="mt-0.5 text-xs text-tertiary">
                        {J.requester}: {r.requester || '—'}
                        {r.approver && ` · ${J.approver}: ${r.approver}`}
                        {r.expires_at && ` · ${J.expires}: ${new Date(r.expires_at).toLocaleString()}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      {r.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            onClick={() => act(() => approveRequest(r.id, dur))}
                            className="rounded-md bg-success px-2.5 py-1 text-xs text-white"
                          >
                            {J.approve}
                          </button>
                          <button
                            type="button"
                            onClick={() => act(() => denyRequest(r.id))}
                            className="rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-primary"
                          >
                            {J.deny}
                          </button>
                        </>
                      )}
                      {r.status === 'approved' && r.active && (
                        <button
                          type="button"
                          onClick={() => act(() => revokeRequest(r.id))}
                          className="rounded-md bg-danger px-2.5 py-1 text-xs text-white"
                        >
                          {J.revoke}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
